import {useState} from 'react';
import {isEqual} from 'lodash';
import {Fade} from 'reactstrap';

import MuiIcon from 'components/Icons/MuiIcon';
import {ContentBox} from 'components/Contents';
import {useViewport} from 'contexts/ViewportContext';
import {VIEWPORT_WIDTHS} from 'configs';

import SectionContents from '../SectionContents';

import local from './NonCompliant.module.scss';
import SectionAccordion from '../SectionAccordion';

const SUPPORT_INDUSTRY = [
  {
    icon: 'rocket_launch',
    iconProps: {variant: 'outlined'},
    title: 'Aerospace & Defence',
  },
  {
    icon: 'energy_savings_leaf',
    iconProps: {symbol: true},
    title: 'Energy & Utilities',
  },
  {
    icon: 'construction',
    title: 'Automotive & Transportation',
  },
  {
    icon: 'cardiology',
    iconProps: {symbol: true},
    title: 'Healthcare',
  },
  {
    icon: 'attach_money',
    title: 'Banking & Financial Services',
  },
  {
    icon: 'settings',
    title: 'Manufacturing',
  },
  {
    icon: 'location_city',
    title: 'Construction',
  },
  {
    icon: 'devices',
    title: 'Technology & Electronics',
  },
  {
    icon: 'fork_spoon',
    iconProps: {symbol: true},
    title: 'Food & Beverage',
  },

  {
    icon: 'gavel',
    title: 'Government',
  },
  {
    icon: 'medical_services',
    title: 'Medical Devices & Pharma',
  },
  {
    icon: 'toolbar',
    iconProps: {symbol: true},
    title: 'Mining & Natural Resources',
  },
];

const SUPPORT_TEAMS = [
  {
    title: 'Procurement & Vendor Management',
    body: (
      <p>Source only from certified suppliers without slowing purchasing.</p>
    ),
  },
  {
    title: 'Compliance & Quality',
    body: <p>Ensure quality, safety, and environmental compliance.</p>,
  },
  {
    title: 'Supply Chain & Logistics',
    body: <p>Monitor certification status across global networks.</p>,
  },
  {
    title: 'Risk & Governance',
    body: <p>Spot non-compliance early to protect brand and operations.</p>,
  },
  {
    title: 'Sustainability & ESG',
    body: <p>Verify suppliers meet environmental and social commitments.</p>,
  },
  {
    title: 'Finance, Insurance & Legal',
    body: <p>Confirm certifications before contracts or underwriting.</p>,
  },
  {
    title: 'Regulators & Authorities',
    body: <p>Verify compliance for domestic tenders or imports.</p>,
  },
];

const SUPPORT_PROBLEMS = [
  {
    title: 'Fake or Counterfeit Certificates',
    body: (
      <p>
        Suppliers can falsely claim compliance. Our ISO certificate verification
        and accredited checks confirm authenticity, including certificates not
        in the database.
      </p>
    ),
  },
  {
    title: 'Slow, Manual Verification',
    body: (
      <p>
        Paperwork wastes time. We deliver instant ISO certification search for
        single or bulk checks (10,000+ companies at once).
      </p>
    ),
  },
  {
    title: 'No Real-Time Alerts',
    body: (
      <p>
        Suspensions, withdrawals, or expirations often go unnoticed. We alert
        you the moment a certificate changes.
      </p>
    ),
  },
  {
    title: 'Supply Chain Compliance Gaps',
    body: (
      <p>
        One non-compliant supplier can disrupt your business. We continuously
        monitor your full network in one platform.
      </p>
    ),
  },
  {
    title: 'Outdated Audit Readiness',
    body: (
      <p>
        Audits shouldn't be a scramble. We keep certifications live, accurate,
        and audit-ready year-round.
      </p>
    ),
  },
  {
    title: 'Fragmented Global Data',
    body: (
      <p>
        Certification data is scattered across thousands of bodies. We unify it
        into one authoritative, globally mandated platform.
      </p>
    ),
  },
];

const SUPPORT_CASES = [
  {
    title: 'Verify Suppliers',
    body: (
      <p>
        Verify ISO certification and other accredited certificates to ensure
        vendors meet standards before doing business.
      </p>
    ),
  },
  {
    title: 'Monitor Risk',
    body: (
      <p>
        Stay alerted to certification changes in real time with instant alerts
        on suspensions, withdrawals, or expirations.
      </p>
    ),
  },
  {
    title: 'Screen Customers',
    body: (
      <p>
        Banks, insurers, and regulators can check ISO certification to confirm
        compliance.
      </p>
    ),
  },
  {
    title: 'Audit Readiness',
    body: (
      <p>
        Keep certifications accurate and audit-ready with trusted ISO
        certificate verification and easy ISO certification lookup.
      </p>
    ),
  },
  {
    title: 'Regulatory Enforcement',
    body: (
      <p>
        Authorities can run an ISO certification search to confirm accredited
        certificates for trade and imports.
      </p>
    ),
  },
];

const IconItems = ({list}) => {
  return (
    <div className={local['items--container']}>
      <div>
        <h5>We Support All Industries</h5>
        <p>
          Our solutions are trusted across sectors. Some of the most popular
          industries we support include:
        </p>
      </div>

      <div className={local['items--list']}>
        {list.map((industry, index) => (
          <div key={index} className={[local['items--list-item']]}>
            <MuiIcon
              name={industry.icon}
              size="lg"
              {...(industry.iconProps || {})}
            />
            <p className="mb-0">{industry.title}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const TextItems = ({list}) => {
  return (
    <div>
      {list.map((team, index) => (
        <div className={local['text--list-item']} key={index}>
          <h5>{team.title}</h5>
          <p>{team.body}</p>
        </div>
      ))}
    </div>
  );
};

const ACCORDION_ITEMS = [
  {
    value: 'industry',
    title: 'Industries We Support',
    icon: 'location_city',
    iconProps: {variant: 'outlined'},
    description:
      'Key sectors where our compliance solutions deliver value and reduce risk.',
    content: <IconItems list={SUPPORT_INDUSTRY} />,
  },
  {
    value: 'teams',
    title: 'Teams We Support',
    icon: 'people',
    iconProps: {variant: 'outlined'},
    description:
      'Departments and roles that rely on our compliance and solutions.',
    content: <TextItems list={SUPPORT_TEAMS} />,
  },
  {
    value: 'problems',
    title: 'Problems We Solve',
    icon: 'report_problem',
    iconProps: {variant: 'outlined'},
    description:
      'Critical compliance challenges that put your business at risk.',
    content: <TextItems list={SUPPORT_PROBLEMS} />,
  },
  {
    value: 'cases',
    title: 'Common Use Cases',
    icon: 'fact_check',
    iconProps: {variant: 'outlined'},
    description: 'Specific scenarios where our platform delivers value.',
    content: <TextItems list={SUPPORT_CASES} />,
  },
];

export default function NonCompliant() {
  const {size} = useViewport();
  const isMobile = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['md'];
  const [selected, setSelected] = useState(ACCORDION_ITEMS[0]);
  const toggle = (value) => setSelected(value);

  return (
    <SectionContents
      sizes={{xs: [12, 12], lg: [5, 7]}}
      className={local['non-compliant']}
    >
      <SectionAccordion
        light
        items={ACCORDION_ITEMS}
        selected={selected}
        toggle={toggle}
      />
      {!isMobile && (
        <div>
          {ACCORDION_ITEMS.map((item, index) => {
            const {content} = item;

            if (!isEqual(item, selected)) return null;

            return (
              <Fade key={index} in={isEqual(item, selected)}>
                <ContentBox className={local['fade--body']}>
                  {content}
                </ContentBox>
              </Fade>
            );
          })}
        </div>
      )}
    </SectionContents>
  );
}
